import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import { adAccountRouteFactory } from "./adAccount";
import { platformRouteFactory } from "./platform";
import { interestRouteFactory } from "./interest";
import { interestGroupRouteFactory } from "./interestGroup";
import { callToActionRouteFactory } from "./callToAction";
import {
  adPlatformListLoader,
  adPlatformViewLoader,
} from "../loaders/adPlatform";
import { adAccountListByAdPlatformLoader } from "../loaders/adAccount";
import { platformListByAdPlatformLoader } from "../loaders/platform";
import { interestListByAdPlatformLoader } from "../loaders/interest";
import { interestGroupListByAdPlatformLoader } from "../loaders/interestGroup";
import { callToActionListByAdPlatformLoader } from "../loaders/callToAction";
import { AdPlatformBreadcrumb } from "../../breadcrumbs/adPlatform";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";

const { ADPLATFORM, ADACCOUNT, PLATFORM, INTEREST, INTERESTGROUP, CALLTOACTION } = ENTITY;

export const adPlatformRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const adPlatformRoute = createRoute({
    getParentRoute: () => parent,
    path: ADPLATFORM.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: ADPLATFORM.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: ADPLATFORM.label.upFirstPlural,
    },
  });

  // List adPlatforms

  const adPlatformListRoute = createRoute({
    getParentRoute: () => adPlatformRoute,
    path: "/",
    loader: adPlatformListLoader,
    pendingComponent: () => <EntityListLoading entity={ADPLATFORM} />,
  }).lazy(() => import("./adPlatform.lazy").then((d) => d.AdPlatformListRoute));

  // View adPlatform

  const adPlatformViewRootRoute = createRoute({
    getParentRoute: () => adPlatformRoute,
    path: "$adPlatformId",
    staticData: {
      breadcrumb: AdPlatformBreadcrumb,
    },
    loader: adPlatformViewLoader,
  });

  // View adPlatform Layout

  const adPlatformViewLayoutRoute = createRoute({
    getParentRoute: () => adPlatformViewRootRoute,
    id: "adPlatform-view-layout",
    pendingComponent: () => <EntityViewLoading entity={ADPLATFORM} />,
  }).lazy(() =>
    import("./adPlatform.lazy").then((d) => d.AdPlatformViewLayoutRoute)
  );

  // View adPlatform - General Tab

  const adPlatformViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => adPlatformViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adPlatform.lazy").then(
      (d) => d.AdPlatformViewLayoutGeneralTabRoute
    )
  );

  // View adPlatform - About Tab

  const adPlatformViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => adPlatformViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adPlatform.lazy").then((d) => d.AdPlatformViewLayoutAboutTabRoute)
  );

  // View adPlatform - AdAccounts Tab

  const adPlatformViewLayoutAdAccountListTabRoute = createRoute({
    getParentRoute: () => adPlatformViewLayoutRoute,
    path: ADACCOUNT.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: ADACCOUNT.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: ADACCOUNT.label.upFirstPlural,
    },
    loader: adAccountListByAdPlatformLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adPlatform.lazy").then(
      (d) => d.AdPlatformViewLayoutAdAccountListTabRoute
    )
  );

  // View adPlatform - Platforms Tab

  const adPlatformViewLayoutPlatformListTabRoute = createRoute({
    getParentRoute: () => adPlatformViewLayoutRoute,
    path: PLATFORM.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: PLATFORM.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: PLATFORM.label.upFirstPlural,
    },
    loader: platformListByAdPlatformLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adPlatform.lazy").then(
      (d) => d.AdPlatformViewLayoutPlatformListTabRoute
    )
  );

  // View adPlatform - Interests Tab

  const adPlatformViewLayoutInterestListTabRoute = createRoute({
    getParentRoute: () => adPlatformViewLayoutRoute,
    path: INTEREST.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: INTEREST.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: INTEREST.label.upFirstPlural,
    },
    loader: interestListByAdPlatformLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adPlatform.lazy").then(
      (d) => d.AdPlatformViewLayoutInterestListTabRoute
    )
  );

  // View adPlatform - InterestGroups Tab

  const adPlatformViewLayoutInterestGroupListTabRoute = createRoute({
    getParentRoute: () => adPlatformViewLayoutRoute,
    path: INTERESTGROUP.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: INTERESTGROUP.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: INTERESTGROUP.label.upFirstPlural,
    },
    loader: interestGroupListByAdPlatformLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adPlatform.lazy").then(
      (d) => d.AdPlatformViewLayoutInterestGroupListTabRoute
    )
  );

  // View adPlatform - CallToActions Tab

  const adPlatformViewLayoutCallToActionListTabRoute = createRoute({
    getParentRoute: () => adPlatformViewLayoutRoute,
    path: CALLTOACTION.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: CALLTOACTION.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: CALLTOACTION.label.upFirstPlural,
    },
    loader: callToActionListByAdPlatformLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adPlatform.lazy").then(
      (d) => d.AdPlatformViewLayoutCallToActionListTabRoute
    )
  );

  // AdPlatform Children
  const adAccountRoute = adAccountRouteFactory(adPlatformViewRootRoute);
  const platformRoute = platformRouteFactory(adPlatformViewRootRoute);
  const interestRoute = interestRouteFactory(adPlatformViewRootRoute);
  const interestGroupRoute = interestGroupRouteFactory(adPlatformViewRootRoute);
  const callToActionRoute = callToActionRouteFactory(adPlatformViewRootRoute);
  

  // Route tree
  adPlatformRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [adPlatformListRoute]),
    adPlatformViewRootRoute.addChildren([
      adPlatformViewLayoutRoute.addChildren([
        adPlatformViewLayoutGeneralTabRoute,
        adPlatformViewLayoutAboutTabRoute,
        adPlatformViewLayoutAdAccountListTabRoute,
        adPlatformViewLayoutPlatformListTabRoute,
        adPlatformViewLayoutInterestListTabRoute,
        adPlatformViewLayoutInterestGroupListTabRoute,
        adPlatformViewLayoutCallToActionListTabRoute
      ]),
      adAccountRoute,
      platformRoute,
      interestRoute,
      interestGroupRoute,
      callToActionRoute
    ]),
  ]);

  return adPlatformRoute;
};

const adPlatformRoute = adPlatformRouteFactory();

export default adPlatformRoute;
